import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  MessageCircle, Send, Image, Smile, X, ChevronLeft,
  Users, Loader2, User, MoreVertical, Check, CheckCheck,
  Video, Play, Paperclip, Trash2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Age group info
const AGE_GROUP_INFO = {
  "40s": { name: "40대 채팅방", description: "40대 회원들의 소통 공간", emoji: "💪", minYear: 1977, maxYear: 1986 },
  "50s": { name: "50대 채팅방", description: "50대 회원들의 소통 공간", emoji: "🌟", minYear: 1967, maxYear: 1976 },
  "60s": { name: "60대 채팅방", description: "60대 회원들의 소통 공간", emoji: "🌸", minYear: 1957, maxYear: 1966 },
};

// Emoji picker data
const EMOJI_CATEGORIES = {
  "자주 쓰는": ["😊", "😂", "❤️", "👍", "🙏", "😍", "🥺", "😭", "🤣", "✨"],
  "표정": ["😀", "😃", "😄", "😁", "😅", "🤪", "😜", "😎", "🥳", "😇"],
  "하트": ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💕", "💖"],
};

export const GroupChatPage = () => {
  const { ageGroup } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const groupInfo = AGE_GROUP_INFO[ageGroup] || { name: "채팅방", description: "", emoji: "💬", minYear: 0, maxYear: 9999 };

  // 관리자 판별 (메시지/사진 삭제 권한)
  const isAdmin = !!(user && (user.is_admin || user.role === "admin" || (user.email || "").toLowerCase() === "admin@newcoupling.com"));

  // 채팅 메시지/사진 삭제 (관리자 또는 작성자)
  const handleDeleteMessage = async (msg) => {
    if (!room) return;
    const label = msg.message_type === "image" || msg.message_type === "video" ? "사진/영상" : "메시지";
    if (!window.confirm(`이 ${label}을(를) 삭제할까요? 삭제 후 복구할 수 없습니다.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/chat/rooms/${room.id}/messages/${msg.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "삭제에 실패했습니다.");
      }
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
    } catch (e) {
      alert(e.message || "삭제에 실패했습니다.");
    }
  };

  // 연령대 입장 제한: 60대 회원은 40대 방만 입장 불가 (그 외 연령대는 자유 입장)
  useEffect(() => {
    if (!user) return;
    const by = parseInt(user.birth_year, 10);
    if (!by) return;
    const age = new Date().getFullYear() - by;
    if (age >= 60 && ageGroup === "40s") {
      alert("60대 회원은 40대 채팅방에 입장할 수 없습니다.");
      navigate("/chat/group", { replace: true });
    }
  }, [user, ageGroup, navigate]);

  // Join or create group chat room
  const joinGroupChat = useCallback(async () => {
    if (!token || !ageGroup) return;
    
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/chat/rooms/age-group/${ageGroup}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRoom(data);
        // Fetch messages
        const msgResponse = await fetch(`${API_URL}/api/chat/rooms/${data.id}/messages?limit=100`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (msgResponse.ok) {
          const msgs = await msgResponse.json();
          setMessages(msgs);
        }
      } else if (response.status === 403) {
        // 연령대 입장 제한 등 — 안내 후 채팅방 목록으로
        const err = await response.json().catch(() => ({}));
        alert(err.detail || "이 채팅방에 입장할 수 없습니다.");
        navigate("/chat/group", { replace: true });
      }
    } catch (error) {
      console.error("Failed to join group chat:", error);
    } finally {
      setIsLoading(false);
    }
  }, [token, ageGroup, navigate]);

  useEffect(() => {
    if (isAuthenticated && token) {
      joinGroupChat();
    } else if (isAuthenticated === false) {
      // Only redirect when we're sure user is not authenticated
      const timer = setTimeout(() => {
        if (!isAuthenticated) navigate("/");
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, token, joinGroupChat, navigate]);

  // WebSocket connection
  useEffect(() => {
    if (!room || !token) return;

    const wsUrl = API_URL.replace("http", "ws");
    const ws = new WebSocket(`${wsUrl}/api/ws/chat/${room.id}?token=${token}`);
    
    ws.onopen = () => {
      setWsConnected(true);
      ws.send(JSON.stringify({ type: "read" }));
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === "new_message") {
        setMessages(prev => [...prev, data.message]);
        ws.send(JSON.stringify({ type: "read" }));
      } else if (data.type === "typing") {
        setTypingUsers(prev => {
          if (!prev.includes(data.user_name)) {
            return [...prev, data.user_name];
          }
          return prev;
        });
        setTimeout(() => {
          setTypingUsers(prev => prev.filter(u => u !== data.user_name));
        }, 3000);
      } else if (data.type === "user_joined") {
        setOnlineUsers(prev => {
          if (!prev.find(u => u.id === data.user_id)) {
            return [...prev, { id: data.user_id, name: data.user_name }];
          }
          return prev;
        });
      } else if (data.type === "user_left") {
        setOnlineUsers(prev => prev.filter(u => u.id !== data.user_id));
      } else if (data.type === "messages_read") {
        setMessages(prev => prev.map(msg => ({
          ...msg,
          read_by: msg.read_by?.includes(data.user_id) 
            ? msg.read_by 
            : [...(msg.read_by || []), data.user_id]
        })));
      } else if (data.type === "message_deleted") {
        setMessages(prev => prev.filter(m => m.id !== data.message_id));
      }
    };
    
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);
    
    wsRef.current = ws;
    
    return () => ws.close();
  }, [room, token]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !room || isSending) return;
    
    setIsSending(true);
    
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "message",
          content: newMessage,
          message_type: "text"
        }));
        setNewMessage("");
      } else {
        const response = await fetch(`${API_URL}/api/chat/rooms/${room.id}/messages`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ content: newMessage, message_type: "text" })
        });
        if (response.ok) {
          const msg = await response.json();
          setMessages(prev => [...prev, msg]);
          setNewMessage("");
        }
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setIsSending(false);
    }
  };

  // Typing indicator
  const handleTyping = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      wsRef.current.send(JSON.stringify({ type: "typing" }));
      typingTimeoutRef.current = setTimeout(() => {}, 3000);
    }
  };

  // Emoji select
  const handleEmojiSelect = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Media upload (image or video)
  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !room) return;
    
    // Check file type
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    
    if (!isImage && !isVideo) {
      alert("이미지 또는 동영상 파일만 업로드할 수 있습니다.");
      return;
    }
    
    // Check file size (50MB max for video, 10MB for image)
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(isVideo ? "동영상은 50MB 이하만 업로드 가능합니다." : "이미지는 10MB 이하만 업로드 가능합니다.");
      return;
    }
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      setIsSending(true);
      console.log("Uploading media file...");
      
      const uploadResponse = await fetch(`${API_URL}/api/chat/upload-media`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      });
      
      console.log("Upload response status:", uploadResponse.status);
      
      if (uploadResponse.ok) {
        const { url, type } = await uploadResponse.json();
        console.log("Upload successful:", url, type);
        
        const messageType = type === "video" ? "video" : "image";
        const content = type === "video" ? "동영상" : "이미지";
        
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          console.log("Sending via WebSocket");
          wsRef.current.send(JSON.stringify({
            type: "message",
            content: content,
            message_type: messageType,
            image_url: url
          }));
        } else {
          console.log("WebSocket not connected, using REST API fallback");
          // REST API fallback
          const msgResponse = await fetch(`${API_URL}/api/chat/rooms/${room.id}/messages`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              content: content,
              message_type: messageType,
              image_url: url
            })
          });
          
          if (msgResponse.ok) {
            const newMsg = await msgResponse.json();
            console.log("Message sent via REST:", newMsg);
            // Add message to local state
            setMessages(prev => [...prev, newMsg]);
          } else {
            console.error("Failed to send message via REST");
            alert("메시지 전송에 실패했습니다.");
          }
        }
      } else {
        const errorText = await uploadResponse.text();
        console.error("Upload failed:", errorText);
        alert("파일 업로드에 실패했습니다.");
      }
    } catch (error) {
      console.error("Failed to upload media:", error);
      alert("파일 업로드 중 오류가 발생했습니다.");
    } finally {
      setIsSending(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
  };

  if (!isAuthenticated || isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center pt-16">
        <Loader2 className="w-8 h-8 animate-spin text-stone-500" />
      </div>
    );
  }

  // 현재 온라인 상태인 참여자만 표시 (본인 포함, 중복 제거)
  const myName = user?.username || user?.name;
  const onlineNameSet = new Set(onlineUsers.map((u) => u.name).filter(Boolean));
  if (myName) onlineNameSet.add(myName);
  const onlineNames = Array.from(
    new Set((room?.participant_names || []).filter((n) => onlineNameSet.has(n)))
  );
  if (myName && !onlineNames.includes(myName)) onlineNames.unshift(myName);

  return (
    <div className="min-h-screen bg-[#FAFAF9] pt-16">
      <div className="max-w-6xl mx-auto h-[calc(100vh-64px)] flex gap-4 px-4 py-2">
        {/* Left Sidebar - Online Users */}
        <aside className="w-56 flex-shrink-0 hidden lg:flex flex-col bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden" data-testid="online-users-sidebar">
          <div className="bg-stone-200 px-4 py-3">
            <h3 className="text-stone-800 font-semibold text-sm flex items-center gap-2">
              <Users className="w-4 h-4" />
              참여자
            </h3>
            <p className="text-stone-500 text-xs mt-0.5">
              {onlineNames.length}명 온라인
            </p>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {onlineNames.map((name, idx) => {
              const isMe = name === user?.username || name === user?.name;
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-xl transition-colors ${
                    isMe ? "bg-stone-100" : "hover:bg-stone-50"
                  }`}
                  data-testid={`participant-${idx}`}
                >
                  <div className="relative flex-shrink-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                      isMe ? "bg-stone-600 text-white" : "bg-stone-200 text-stone-500"
                    }`}>
                      {name.charAt(0)}
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white bg-green-500" style={{ boxShadow: "0 0 6px rgba(34,197,94,0.6)" }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm truncate ${isMe ? "font-semibold text-stone-800" : "text-stone-700"}`}>
                      {name}{isMe ? " (나)" : ""}
                    </p>
                    <p className="text-xs font-medium text-green-600">
                      ON
                    </p>
                  </div>
                </div>
              );
            })}
            {onlineNames.length === 0 && (
              <p className="text-xs text-stone-400 text-center py-4">온라인 참여자 없음</p>
            )}
          </div>
        </aside>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
          {/* Header */}
          <div className="bg-stone-200 text-stone-800 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link
                  to="/chat/group"
                  className="p-2 hover:bg-stone-300 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Link>
                <div className="w-12 h-12 bg-stone-300 rounded-full flex items-center justify-center text-2xl">
                  {groupInfo.emoji}
                </div>
                <div>
                  <h1 className="text-lg font-bold">{groupInfo.name}</h1>
                  <p className="text-sm text-stone-500">{groupInfo.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowParticipants(!showParticipants)}
                  className="flex lg:hidden items-center gap-2 px-3 py-1.5 bg-stone-300 hover:bg-stone-400 rounded-full text-sm transition-colors"
                >
                  <Users className="w-4 h-4" />
                  <span>{room?.participant_ids?.length || 0}명</span>
                </button>
              </div>
            </div>
            <div className="mt-2 text-xs text-stone-500 flex items-center gap-4">
              <span className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-400' : 'bg-yellow-400'}`}></span>
                {wsConnected ? "실시간 연결됨" : "연결 중..."}
              </span>
              {typingUsers.length > 0 && (
                <span className="text-stone-600">
                  {typingUsers.join(", ")} 입력 중...
                </span>
              )}
            </div>
          </div>

          {/* Mobile Participants Panel */}
          {showParticipants && (
            <div className="bg-stone-50 border-b border-stone-200 p-4 lg:hidden">
              <h3 className="text-sm font-semibold text-stone-700 mb-2">온라인 참여자 ({onlineNames.length}명)</h3>
              <div className="flex flex-wrap gap-2">
                {onlineNames.map((name, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-white rounded-full text-sm text-stone-600 border border-stone-200 flex items-center gap-1.5"
                  >
                    <span className="w-2 h-2 rounded-full bg-green-400" />
                    {name}
                  </span>
                ))}
                {onlineNames.length === 0 && (
                  <span className="text-xs text-stone-400">온라인 참여자 없음</span>
                )}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-stone-50">
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">{groupInfo.emoji}</div>
                <h2 className="text-xl font-semibold text-stone-600 mb-2">{groupInfo.name}에 오신 것을 환영합니다!</h2>
                <p className="text-stone-400">첫 메시지를 남겨보세요</p>
              </div>
            ) : (
              messages.map((msg, idx) => {
                const isMe = msg.sender_id === user?.id;
                const showDate = idx === 0 || 
                  new Date(messages[idx-1].created_at).toDateString() !== new Date(msg.created_at).toDateString();
                
                return (
                  <div key={msg.id}>
                    {showDate && (
                      <div className="text-center my-4">
                        <span className="bg-stone-200 text-stone-600 text-xs px-3 py-1 rounded-full">
                          {new Date(msg.created_at).toLocaleDateString("ko-KR", {
                            year: "numeric", month: "long", day: "numeric"
                          })}
                        </span>
                      </div>
                    )}
                    <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] ${isMe ? 'order-2' : ''}`}>
                        {!isMe && (
                          <p className="text-xs text-stone-500 mb-1 ml-1 font-medium">{msg.sender_name}</p>
                        )}
                        <div className={`flex items-end gap-2 ${isMe ? 'flex-row-reverse' : ''}`}>
                          <div className={`rounded-2xl px-4 py-2 ${
                            isMe 
                              ? 'bg-stone-600 text-white rounded-br-sm' 
                              : 'bg-white text-stone-800 rounded-bl-sm shadow-sm'
                          }`}>
                            {msg.message_type === "image" ? (
                              <img 
                                src={`${API_URL}${msg.image_url}`} 
                                alt="이미지" 
                                className="max-w-[200px] rounded-lg cursor-pointer"
                                onClick={() => window.open(`${API_URL}${msg.image_url}`, '_blank')}
                              />
                            ) : msg.message_type === "video" ? (
                              <div className="relative">
                                <video 
                                  src={`${API_URL}${msg.image_url}`}
                                  className="max-w-[280px] rounded-lg"
                                  controls
                                  preload="metadata"
                                />
                              </div>
                            ) : (
                              <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                            )}
                          </div>
                          <span className="text-xs text-stone-400">{formatTime(msg.created_at)}</span>
                          {(isAdmin || isMe) && (
                            <button
                              type="button"
                              onClick={() => handleDeleteMessage(msg)}
                              className="p-1 rounded-full text-stone-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                              title={isAdmin && !isMe ? "관리자 삭제" : "삭제"}
                              data-testid={`chat-delete-${msg.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-stone-200 bg-white">
            <form onSubmit={handleSendMessage} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                title="사진/동영상 전송"
              >
                <Paperclip className="w-5 h-5 text-stone-500" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleMediaUpload}
                className="hidden"
              />
              
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <Smile className="w-5 h-5 text-stone-500" />
                </button>
                
                {showEmojiPicker && (
                  <div className="absolute bottom-12 left-0 bg-white rounded-xl shadow-xl border border-stone-200 p-3 w-72 z-10">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-semibold text-stone-700">이모티콘</span>
                      <button onClick={() => setShowEmojiPicker(false)}>
                        <X className="w-4 h-4 text-stone-400" />
                      </button>
                    </div>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {Object.entries(EMOJI_CATEGORIES).map(([category, emojis]) => (
                        <div key={category}>
                          <p className="text-xs text-stone-500 mb-1">{category}</p>
                          <div className="flex flex-wrap gap-1">
                            {emojis.map(emoji => (
                              <button
                                key={emoji}
                                type="button"
                                onClick={() => handleEmojiSelect(emoji)}
                                className="w-8 h-8 flex items-center justify-center hover:bg-stone-100 rounded text-lg"
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <input
                type="text"
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  handleTyping();
                }}
                placeholder="메시지를 입력하세요..."
                className="flex-1 px-4 py-2 bg-stone-100 rounded-full focus:outline-none focus:ring-2 focus:ring-stone-300"
                data-testid="group-chat-input"
              />
              
              <Button
                type="submit"
                disabled={!newMessage.trim() || isSending}
                className="rounded-full w-10 h-10 p-0 bg-stone-600 hover:bg-stone-700 text-white"
                data-testid="group-send-btn"
              >
                {isSending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupChatPage;
